import logger, { LogCategory } from '@/lib/logging';

let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!sharedCtx || sharedCtx.state === 'closed') {
      sharedCtx = new Ctor();
    }
    return sharedCtx;
  } catch (e) {
    logger.debug('Posture capture sound: AudioContext unavailable', {
      category: LogCategory.UI,
      camera: true,
      message: (e as Error)?.message,
    });
    return null;
  }
}

function resumeIfSuspended(ctx: AudioContext): Promise<void> {
  if (ctx.state !== 'suspended') return Promise.resolve();
  return ctx.resume().catch((e: unknown) => {
    logger.debug('Posture capture sound: context resume skipped', {
      category: LogCategory.UI,
      camera: true,
      message: (e as Error)?.message,
    });
  });
}

/**
 * Short “lock-in” chime when the capture countdown overlay starts (auto-capture engaged).
 * Uses Web Audio so we ship no separate sound files.
 */
export function playPostureCountdownLockSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  void resumeIfSuspended(ctx).then(() => {
    const t0 = ctx.currentTime;
    const freqs = [880, 659.25];
    freqs.forEach((freq, i) => {
      const start = t0 + i * 0.11;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.22);
    });
  });
}

/**
 * Short tick when the on-screen countdown advances (e.g. 3 → 2 → 1).
 * `secondsRemaining` is the new value shown (always ≥ 1); no tick at 0 — shutter handles capture.
 */
export function playPostureCountdownTickSound(secondsRemaining: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  void resumeIfSuspended(ctx).then(() => {
    const t0 = ctx.currentTime;
    const base = 480;
    const freq = base + Math.max(0, 4 - secondsRemaining) * 55;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.14, t0 + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.13);
  });
}

/**
 * Brief shutter-like click + decayed noise when a live frame is grabbed from the camera.
 */
export function playPostureShutterSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  void resumeIfSuspended(ctx).then(() => {
    const t0 = ctx.currentTime;

    const click = ctx.createOscillator();
    const clickGain = ctx.createGain();
    click.type = 'triangle';
    click.frequency.setValueAtTime(2400, t0);
    click.frequency.exponentialRampToValueAtTime(400, t0 + 0.028);
    clickGain.gain.setValueAtTime(0.0001, t0);
    clickGain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.008);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.035);
    click.connect(clickGain);
    clickGain.connect(ctx.destination);
    click.start(t0);
    click.stop(t0 + 0.04);

    const nSamples = Math.floor(ctx.sampleRate * 0.042);
    const buffer = ctx.createBuffer(1, nSamples, ctx.sampleRate);
    const ch0 = buffer.getChannelData(0);
    for (let i = 0; i < nSamples; i++) {
      const env = 1 - i / nSamples;
      ch0[i] = (Math.random() * 2 - 1) * env * env;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, t0);
    noiseGain.gain.exponentialRampToValueAtTime(0.14, t0 + 0.004);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);
    noise.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noise.start(t0);
    noise.stop(t0 + 0.045);
  });
}

/**
 * Short ascending success motif when the guided capture set is complete and we exit the camera.
 */
export function playPostureGuidedCompleteSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  void resumeIfSuspended(ctx).then(() => {
    const t0 = ctx.currentTime;
    const freqs = [523.25, 659.25, 783.99];
    freqs.forEach((freq, i) => {
      const start = t0 + i * 0.08;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.22);
    });
  });
}
